import { useState } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Field, Empty } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money, useLoad } from "@/lib/api";
import { CATEGORY_LABELS, EXPENSE_CATEGORIES, METHOD_LABELS } from "@/lib/constants";

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

function downloadCsv(name, rows) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = name;
  a.click();
}

const Breakdown = ({ title, rows, labels, cur, testId }) => (
  <Panel title={title} testId={testId}>
    {!rows?.length ? <Empty text="No data." /> : (
      <ul className="space-y-2 text-sm">{rows.map((r) => (
        <li key={r.name} className="flex justify-between"><span>{labels[r.name] || r.name}</span><span className="font-mono">{money(r.value, cur)}</span></li>))}</ul>)}
  </Panel>
);

export default function Reports() {
  const { user, clinics } = useAuth();
  const [scope, setScope] = useState(user.role === "ceo" ? "all" : clinics[0]?.id || "all");
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [d] = useLoad(clinics.length ? "/dashboard" : null, { clinic_id: scope, start, end });
  const cur = d?.currencies?.length === 1 ? d.currencies[0] : null;
  const [busy, setBusy] = useState(false);
  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/pdf", { params: { clinic_id: scope, start, end }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `klinic-report-${start || "all"}-to-${end}.pdf`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <PageHeader eyebrow="Financial & operational" title="Reports" subtitle="Income statement, cash flow and activity for any period. Download as PDF or CSV for your accountant or board.">
        <Button variant="outline" className="rounded-full" onClick={() => downloadCsv(`clinic-report-${start}-to-${end}.csv`, d?.clinics || [])} data-testid="report-export-csv-button"><Download className="mr-2 h-4 w-4" />Export CSV</Button>
        <Button className="rounded-full" disabled={busy || !clinics.length} onClick={downloadPdf} data-testid="report-export-pdf-button"><FileText className="mr-2 h-4 w-4" />{busy ? "Generating…" : "Download PDF"}</Button>
      </PageHeader>
      <Panel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Clinic"><Select value={scope} onValueChange={setScope} disabled={user.role !== "ceo"}>
            <SelectTrigger data-testid="report-clinic-select"><SelectValue /></SelectTrigger>
            <SelectContent>{user.role === "ceo" && <SelectItem value="all">All clinics</SelectItem>}{clinics.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="From"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-input" /></Field>
          <Field label="To"><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-input" /></Field>
        </div>
      </Panel>
      <Panel title="Income statement by clinic" testId="report-summary-table">
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Clinic</TableHead><TableHead>Currency</TableHead><TableHead className="text-right">Invoiced</TableHead><TableHead className="text-right">Collected</TableHead><TableHead className="text-right">Expenses</TableHead><TableHead className="text-right">Net cash flow</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Visits</TableHead><TableHead className="text-right">New patients</TableHead></TableRow></TableHeader>
          <TableBody>
            {(d?.clinics || []).map((r) => (
              <TableRow key={r.clinic_id}><TableCell className="font-semibold">{r.clinic}</TableCell><TableCell className="font-mono text-xs">{r.currency}</TableCell>
                <TableCell className="text-right">{money(r.invoiced, r.currency)}</TableCell><TableCell className="text-right text-emerald-700">{money(r.income, r.currency)}</TableCell>
                <TableCell className="text-right text-rose-600">{money(r.expenses, r.currency)}</TableCell><TableCell className="text-right font-semibold">{money(r.net, r.currency)}</TableCell>
                <TableCell className="text-right">{money(r.outstanding, r.currency)}</TableCell><TableCell className="text-right">{r.visits}</TableCell><TableCell className="text-right">{r.patients}</TableCell></TableRow>))}
            {(d?.totals || []).map((t) => (
              <TableRow key={t.currency} className="bg-slate-50 font-semibold" data-testid={`report-total-${t.currency}`}><TableCell>Total</TableCell><TableCell className="font-mono text-xs">{t.currency}</TableCell>
                <TableCell className="text-right">{money(t.invoiced, t.currency)}</TableCell><TableCell className="text-right">{money(t.income, t.currency)}</TableCell>
                <TableCell className="text-right">{money(t.expenses, t.currency)}</TableCell><TableCell className="text-right">{money(t.net, t.currency)}</TableCell>
                <TableCell className="text-right">{money(t.outstanding, t.currency)}</TableCell><TableCell /><TableCell /></TableRow>))}
          </TableBody>
        </Table></div>
      </Panel>
      {cur ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Breakdown title="Collections by method" rows={d.by_method} labels={METHOD_LABELS} cur={cur} testId="report-by-method" />
          <Breakdown title="Billing by service line" rows={d.by_category} labels={CATEGORY_LABELS} cur={cur} testId="report-by-category" />
          <Breakdown title="Expenses by category" rows={d.expense_by_category} labels={EXPENSE_CATEGORIES} cur={cur} testId="report-expense-category" />
        </div>
      ) : <p className="text-sm text-slate-500">Select a single clinic to see breakdowns (clinics use different currencies).</p>}
    </>
  );
}
